import { Detector } from '../engine/Component';
import { monochromaticToRGB, rgbToCSS } from '../utils/color';

interface PinnedPanel {
  detector: Detector;
  element: HTMLElement;
  profileCanvas: HTMLCanvasElement;
  traceCanvas: HTMLCanvasElement;
}

/**
 * Manages pinned detector output panels that remain visible
 * regardless of component selection.
 */
export class PinnedDetectorManager {
  private container: HTMLElement;
  private panels: Map<number, PinnedPanel> = new Map();
  private updateId: number = 0;

  constructor(container: HTMLElement) {
    this.container = container;
    this.startUpdates();
  }

  isPinned(detectorId: number): boolean {
    return this.panels.has(detectorId);
  }

  pin(detector: Detector): void {
    if (this.panels.has(detector.id)) return;

    const el = document.createElement('div');
    el.className = 'pinned-detector';
    el.innerHTML = `
      <div class="pinned-header">
        <span class="pinned-title">Detector #${detector.id}</span>
        <button class="pinned-close" title="Unpin">&times;</button>
      </div>
      <canvas class="pinned-profile" width="180" height="50"></canvas>
      <canvas class="pinned-trace" width="180" height="40"></canvas>
    `;

    const closeBtn = el.querySelector('.pinned-close')!;
    closeBtn.addEventListener('click', () => this.unpin(detector.id));

    this.container.appendChild(el);

    const panel: PinnedPanel = {
      detector,
      element: el,
      profileCanvas: el.querySelector('.pinned-profile') as HTMLCanvasElement,
      traceCanvas: el.querySelector('.pinned-trace') as HTMLCanvasElement,
    };
    this.panels.set(detector.id, panel);
  }

  unpin(detectorId: number): void {
    const panel = this.panels.get(detectorId);
    if (!panel) return;
    panel.element.remove();
    this.panels.delete(detectorId);
  }

  toggle(detector: Detector): void {
    if (this.isPinned(detector.id)) {
      this.unpin(detector.id);
    } else {
      this.pin(detector);
    }
  }

  /** Remove panels whose detectors are no longer in the scene */
  cleanup(sceneDetectorIds: Set<number>): void {
    for (const [id] of this.panels) {
      if (!sceneDetectorIds.has(id)) {
        this.unpin(id);
      }
    }
  }

  private startUpdates(): void {
    const update = () => {
      for (const [, panel] of this.panels) {
        this.drawProfile(panel);
        this.drawTrace(panel);
      }
      this.updateId = requestAnimationFrame(update);
    };
    this.updateId = requestAnimationFrame(update);
  }

  destroy(): void {
    cancelAnimationFrame(this.updateId);
    for (const [, panel] of this.panels) {
      panel.element.remove();
    }
    this.panels.clear();
  }

  private drawProfile(panel: PinnedPanel): void {
    const { detector, profileCanvas: canvas } = panel;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const maxI = detector.getMaxIntensity();
    if (maxI < 1e-10) return;

    const barWidth = w / detector.bins.length;
    for (let i = 0; i < detector.bins.length; i++) {
      const computedI = detector.getComputedIntensity(i);
      if (computedI < 1e-10) continue;
      const barHeight = (computedI / maxI) * (h - 2);
      const bin = detector.bins[i];
      const avgWl = bin.wavelengthWeighted > 0 ? bin.wavelengthWeighted / (bin.intensity || computedI) : 550e-9;
      const rgb = monochromaticToRGB(avgWl);
      ctx.fillStyle = rgbToCSS(rgb, 0.9);
      ctx.fillRect(i * barWidth, h - barHeight, barWidth, barHeight);
    }
  }

  private drawTrace(panel: PinnedPanel): void {
    const { detector, traceCanvas: canvas } = panel;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const history = detector.intensityHistory;
    if (history.length < 2) return;
    const peak = detector.getHistoryPeak();
    if (peak < 1e-10) return;

    ctx.beginPath();
    for (let i = 0; i < history.length; i++) {
      const x = (i / (history.length - 1)) * w;
      const y = h * (1 - history[i] / peak);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#44dd88';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Current value
    const current = history[history.length - 1];
    ctx.font = '8px monospace';
    ctx.fillStyle = '#44dd88';
    ctx.textAlign = 'right';
    ctx.fillText(current.toFixed(3), w - 2, 9);
    ctx.textAlign = 'left';
  }
}
